// 资源类型定义
export interface Resource {
  id: string;
  type: 'image' | 'text';
  content: string;
  name: string;
  order: number;
  // 可选：图片资源的宽高（与 globalState.Resource 对齐）
  width?: number;
  height?: number;
}

export interface TransformedLayer {
  id: string;
  type: 'public' | 'print_area' | 'Image' | 'text' | 'frame' | 'Circle';
  src?: string;
  text?: string;
  ai_url?: string;
  ai?: string;
  shapeType?: string;
  refresh?: boolean;
  fill?: any;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  shadow?: string;
  transform?: string;

  left: number;
  top: number;
  width: number;
  height: number;
  angle?: number;
  scaleX: number;
  scaleY: number;

  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  textAlign?: string;


  lock?: boolean;

  points?: any;
  options:{
    left:number,
    top:number,
    width:number,
    height:number,
  }

  effectData?: any;
  effectType?: string;
}



// Interface definitions for API response
export interface ApiProduct {
  id: number;
  name: string;
  code?: string;
  description: string;
  category: string;
  basePrice: number;
  price: number;
  image?: string;
  optionCombinations?: any[];
  isActive: boolean;
  isFeatured: boolean;
  sliderImages?: string;
  sortOrder: number;
  tags: string[];
  previews?: any;
  templates?: ApiTemplate[];
  rating?: number;
  reviews?: number;
}

export interface ApiTemplate {
  id: number;
  name: string;
  description: string;
  price: number;
  isActive: boolean;
  isPremium: boolean;
  sortOrder: number;
  design?: any;
}

export interface ProductsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    products: ApiProduct[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}
/**
 * 按顺序替换layers中的内容
 * @param layers - 图层数组
 * @param resources - 资源数组
 * @returns 更新后的图层数组
 */
export const addResource = (layers: TransformedLayer[], resources: Resource[]): TransformedLayer[] => {
  const updatedLayers = [...layers];

  // 遍历layers，按顺序替换符合条件的图层
  // 记录已经被替换的图层索引
  const replacedLayerIndices = new Set<number>();

  // 逐个遍历 resources
  for (const resource of resources) {
    // 先收集所有符合条件的图层索引
    const eligibleIndices: number[] = [];
    for (let i = 0; i < updatedLayers.length; i++) {
      const layer = updatedLayers[i];
      const typeMatches =
        (layer.type === 'text' && resource.type === 'text') ||
        ((layer.type === 'Image' || layer.type === 'frame') && resource.type === 'image');

      if (!replacedLayerIndices.has(i) && layer.lock !== true && typeMatches) {
        eligibleIndices.push(i);
      }
    }

    // 优先选择 focus === true 的图层
    let targetIndex = eligibleIndices.find((idx) => (updatedLayers[idx] as any)?.focus === true);
    if (typeof targetIndex !== 'number') {
      // 如果没有 focus 图层，回退到第一个符合条件的图层
      targetIndex = eligibleIndices.length > 0 ? eligibleIndices[0] : undefined as any;
    }

    if (typeof targetIndex !== 'number') {
      // 本资源没有可替换的图层
      continue;
    }

    const layer = updatedLayers[targetIndex];

    // 执行替换
    if (layer.type === 'text' && resource.type === 'text') {
      // 文本替换文本，并调整字体大小
      const originalText = layer.text || '';
      const newContent = resource.content;
      const originalFontSize = layer.fontSize || 16;

      // 计算文本长度比例
      const textRatio = originalText.length > 0 ? newContent.length / originalText.length : 1;
      const newFontSize = Math.max(8, Math.min(originalFontSize / textRatio, originalFontSize * 2));

      updatedLayers[targetIndex] = {
        ...layer,
        text: resource.content
      };
    } else if ((layer.type === 'Image' || layer.type === 'frame') && resource.type === 'image') {
      // 图片替换图片/相框
      updatedLayers[targetIndex] = {
        ...layer,
        src: resource.content
      };
    }

    // 标记该图层已被替换
    replacedLayerIndices.add(targetIndex);
  }

  return updatedLayers;
};

/**
 * 获取可替换的图层数量
 * @param layers - 图层数组
 * @returns 可替换的图层数量
 */
export const getReplaceableLayerCount = (layers: TransformedLayer[]): number => {
  return layers.filter(layer =>
    (layer.type === 'Image' || layer.type === 'text' || layer.type === 'frame') &&
    layer.lock !== true
  ).length;
};

/**
 * 获取图层类型对应的资源类型
 * @param layerType - 图层类型
 * @returns 对应的资源类型
 */
export const getResourceTypeForLayer = (layerType: string): 'image' | 'text' | null => {
  switch (layerType) {
    case 'text':
      return 'text';
    case 'image':
    case 'frame':
      return 'image';
    default:
      return null;
  }
};

